"use client"

import createMenuStructure, { MenuItem } from "@/components/create-menu-structure"
import { MenuSubItem } from "@/components/header-group/components/MenuSubItem"
import { getActiveMenuItemId, processMenuUrl } from "@/components/header-group/utils"
import { LanguageLabel } from "@/components/language-label"
import { getConfigs, languages } from "@/configs"
import { Images } from "@/constants/images"
import { useLocaleSwitcherLinks } from "@/hook/use-locale-switcher-links"
import { t } from "@/languages/i18n"
import { getLocaleHomeHref } from "@/lib/locale-utils"
import { getMainMenu } from "@/lib/navigation-services"
import { getSiteIdFromHost } from "@/sites/config"
import { CaretDownOutlined } from "@ant-design/icons"
import { message } from "antd"
import { Menu, X } from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { useEffect, useState } from "react"
import { DataSettingItem } from "../lib/config/types"

interface Menu {
  attributes: {
    items: MenuItem[]
  }
}

type HeaderProps = {
  locale: string
  menuData?: Menu
}

const Header: React.FC<HeaderProps> = ({ locale }) => {
  const [menuData, setMenuData] = useState<Menu>()
  const [activeItemId, setActiveItemId] = useState<number | null>(null)
  const [settingsData, setSettingsData] = useState<DataSettingItem[]>([])
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isMenuClosing, setIsMenuClosing] = useState(false)
  const [openSubMenus, setOpenSubMenus] = useState<Set<number>>(new Set())
  const pathname = usePathname()
  const siteId = getSiteIdFromHost(window.location.hostname)
  const configs = getConfigs(siteId)

  const handleCloseMenu = () => {
    setIsMenuClosing(true)
    setTimeout(() => {
      setIsMobileMenuOpen(false)
      setIsMenuClosing(false)
      setOpenSubMenus(new Set())
    }, 300)
  }

  const handleToggleSubMenu = (itemId: number) => {
    const newOpenSubMenus = new Set(openSubMenus)
    if (newOpenSubMenus.has(itemId)) {
      newOpenSubMenus.delete(itemId)
    } else {
      newOpenSubMenus.add(itemId)
    }
    setOpenSubMenus(newOpenSubMenus)
  }

  const getMenuData = async () => {
    try {
      const response = await getMainMenu(configs.HEADER_PAGE_ID.vi, locale)
      setMenuData(response && response)
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error?.message || error?.message || t("Failed to fetch data. Please try again.")
      message.error(errorMessage)
    }
  }

  const localeLinks = useLocaleSwitcherLinks(locale)

  const structuredMenu = createMenuStructure(menuData && menuData.attributes.items)
  const homeHref = getLocaleHomeHref(locale)

  useEffect(() => {
    getMenuData()
  }, [])

  useEffect(() => {
    const activeId = getActiveMenuItemId(structuredMenu, pathname, locale)
    setActiveItemId(activeId)
  }, [pathname, structuredMenu, locale])

  return (
    <>
      <div className="fixed top-0 left-0 right-0 bg-white shadow-2xl hidden lg:block z-50">
        <div className="flex flex-col gap-3">
          <div className="max-w-full w-[1290px] mx-auto flex justify-between h-[74px] items-center">
            <div className="sm:w-[20%] px-[15px] image-hover">
              <div className="w-[180px] h-[17px]">
                <Link href={homeHref}>
                  <Image alt="Logo" src={Images.logoTamdaGroup} className="w-full h-full object-contain" />
                </Link>
              </div>
            </div>
            <div className="sm:w-[67.3%] px-[15px]">
              <nav className="relative">
                <ul className="flex justify-center gap-10 h-[70px]">
                  {structuredMenu?.map(item => {
                    const hasSubmenu = item.attribute && item.attribute.length > 0
                    const { finalUrl, isExternal } = processMenuUrl(item.url, locale)

                    const linkClassName = `relative flex gap-[6px] text-base leading-[22px] font-bold items-center text-[#242f3e] group after:block after:absolute after:bottom-[-3px] after:left-0 after:right-0 after:h-[3px] after:bg-[#18af4e] after:scale-x-0 after:transition-transform after:duration-500 after:origin-left hover:after:scale-x-100 ${
                      activeItemId === item.id ? " text-[#18af4e] after:scale-x-100 after:bg-[#18af4e]" : ""
                    } before:absolute before:bottom-[-27px] before:left-0 before:right-0 before:bg-transparent`

                    return isExternal ? (
                      <Link
                        key={item.id}
                        href={finalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={linkClassName}
                      >
                        {item.title}
                        {hasSubmenu && <CaretDownOutlined className="text-[10px] leading-4 " />}
                        {hasSubmenu && item.attribute && item.attribute.length > 0 && (
                          <ul className="hidden absolute py-5 font-normal group-hover:inline-block top-[72px] left-0 bg-white z-50 duration-300 outline-none border-none text-nowrap pointer-events-none group-hover:pointer-events-auto group-hover:shadow-lg before:absolute before:left-0 before:right-0 before:bg-transparent">
                            {item.attribute.map((subItem, index) => (
                              <li key={index}>
                                <MenuSubItem
                                  subItem={subItem}
                                  locale={locale}
                                  className="block text-[#242f3e] text-[15px] leading-[31px] font-bold px-5 sm:py-2 hover:bg-gray-100 hover:text-[#18af4e] transition-all duration-300"
                                />
                              </li>
                            ))}
                          </ul>
                        )}
                      </Link>
                    ) : (
                      <Link key={item.id} href={finalUrl} className={linkClassName}>
                        {item.title}
                        {hasSubmenu && <CaretDownOutlined className="text-[10px] leading-4 " />}
                        {hasSubmenu && item.attribute && item.attribute.length > 0 && (
                          <ul className="hidden absolute py-5 font-normal group-hover:inline-block top-[72px] left-0 bg-white z-50 duration-300 outline-none border-none text-nowrap pointer-events-none group-hover:pointer-events-auto group-hover:shadow-lg before:absolute before:left-0 before:right-0 before:bg-transparent">
                            {item.attribute.map((subItem, index) => (
                              <li key={index}>
                                <MenuSubItem
                                  subItem={subItem}
                                  locale={locale}
                                  className="block text-[#242f3e] text-[15px] leading-[31px] font-bold px-5 sm:py-2 hover:bg-gray-100 hover:text-[#18af4e] transition-all duration-300"
                                />
                              </li>
                            ))}
                          </ul>
                        )}
                      </Link>
                    )
                  })}
                </ul>
              </nav>
            </div>
            <div className="sm:w-[12.7%] px-[15px] h-[70px] flex items-center justify-end">
              <div className="relative group h-full flex items-center justify-center cursor-pointer after:block after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[3px] after:bg-[#18af4e] after:scale-x-0 after:transition-transform after:duration-500 after:origin-left hover:after:scale-x-100">
                <div className="cursor-pointer">
                  {(() => {
                    const currentLang = languages.find(lang => lang.code === locale) || languages[1]
                    return (
                      <LanguageLabel language={currentLang.shortLabel} urlLogo={currentLang.logo} checkShow={true} />
                    )
                  })()}
                </div>
                <ul
                  className="absolute py-2.5 right-0 top-5 bg-white shadow-2xl z-50 min-w-[250px] flex flex-col opacity-0 translate-y-2 pointer-events-none transition-all duration-300 ease-out group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto"
                  aria-hidden="true"
                >
                  {languages.map(lang => (
                    <li key={lang.code}>
                      <Link
                        href={localeLinks[lang.code] ?? lang.href}
                        className="flex items-center px-5 py-2.5 hover:bg-gray-100 hover:text-[#18af4e] transition-all duration-500"
                      >
                        <LanguageLabel language={lang.label} urlLogo={lang.logo} checkShow={false} showIcon={false} />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Spacer for desktop header to prevent content overlap */}
      <div className="hidden lg:block h-[74px]" />
      <div className="fixed top-0 left-0 right-0 block lg:hidden z-50 bg-white">
        <div className="items-center py-5 relative">
          <div className="flex justify-between items-center w-full h-[70px]">
            <div
              className="px-[15px] cursor-pointer"
              onClick={() => {
                if (isMobileMenuOpen) {
                  handleCloseMenu()
                } else {
                  setIsMobileMenuOpen(true)
                }
              }}
            >
              <Menu color="#18af4e" size={33} strokeWidth={1.5} />
            </div>
            <Link className="w-[220px] h-[26px] mx-[15px] flex items-center justify-center" href={homeHref}>
              <Image alt="Logo" className="w-[180px] h-full object-contain" src={Images.logoTamdaGroup} />
            </Link>
            <div className="flex justify-end items-center gap-4 justify-items-center">
              <div className="relative group pr-[15px]">
                <div className="cursor-pointer flex items-center justify-center gap-[5px]">
                  <div className="w-5 h-6">
                    <Image
                      src={locale === "en" ? Images.logoEn : Images.logoVi}
                      alt={`logo-${locale}`}
                      height={20}
                      width={20}
                      className="w-full h-full"
                    />
                  </div>
                  <span className="text-base leading-5 font-bold text-[#242f3e]">{locale === "en" ? "EN" : "VN"}</span>
                </div>
                <ul
                  className="absolute right-[15px] top-10 bg-white shadow-lg z-50 min-w-[150px] opacity-0 translate-y-10 pointer-events-none transition-all duration-700 ease-out group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto shadow-custom-lg"
                  aria-hidden="true"
                >
                  {languages.map(lang => (
                    <li key={lang.code}>
                      <Link
                        href={localeLinks[lang.code] ?? lang.href}
                        className="flex items-center gap-1 px-4 py-3 hover:bg-gray-100"
                      >
                        <div className="w-5 h-6">
                          <Image src={lang.logo} alt={`logo-${lang.code}`} height={20} width={20} />
                        </div>
                        <span className="text-sm leading-5 font-medium text-[#242f3e]">{lang.shortLabel}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          {isMobileMenuOpen && (
            <>
              <div className="fixed inset-0 bg-black bg-opacity-90 z-40" onClick={handleCloseMenu} />
              <div
                className={`fixed top-0 left-0 w-[80%] h-[100vh] bg-[#18af4e] shadow-lg z-50 ${
                  isMenuClosing ? "mobile-menu-slide-out" : "mobile-menu-slide-in"
                }`}
              >
                <div className="h-[85px] border-b-[3px] border-white flex items-center justify-between px-[15px]">
                  <Link href={homeHref} className="w-full flex justify-center">
                    <Image src={Images.logoTamdaGroupMobile} alt="Logo" className="w-[180px] h-[43px] object-contain" />
                  </Link>
                  <button onClick={handleCloseMenu} className="text-white">
                    <X size={32} strokeWidth={2} className="w-10 h-10" />
                  </button>
                </div>
                <ul className="flex flex-col py-5 overflow-y-auto h-[calc(100vh-70px)]">
                  {structuredMenu?.map((item, index) => {
                    const hasSubmenu = item.attribute && item.attribute.length > 0
                    const { finalUrl, isExternal } = processMenuUrl(item.url, locale)

                    return (
                      <li key={item.id}>
                        <div className="flex items-center justify-between px-10 py-6">
                          {isExternal ? (
                            <Link
                              href={finalUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1 text-white text-base leading-[22px] font-bold uppercase"
                              onClick={() => {
                                if (!hasSubmenu) {
                                  handleCloseMenu()
                                }
                              }}
                            >
                              <span className="text-base leading-[22px] font-medium uppercase text-white">
                                {item.title}
                              </span>
                            </Link>
                          ) : (
                            <Link
                              href={finalUrl}
                              className="flex-1 text-white text-base leading-[22px] font-bold uppercase"
                              onClick={e => {
                                if (hasSubmenu) {
                                  if (openSubMenus.has(item.id)) {
                                    handleCloseMenu()
                                  } else {
                                    e.preventDefault()
                                    handleToggleSubMenu(item.id)
                                  }
                                } else {
                                  handleCloseMenu()
                                }
                              }}
                            >
                              <span className="text-base leading-[22px] font-medium uppercase text-white">
                                {item.title}
                              </span>
                            </Link>
                          )}
                          {item.attribute && item.attribute.length > 0 && (
                            <button
                              onClick={e => {
                                e.stopPropagation()
                                handleToggleSubMenu(item.id)
                              }}
                              className="text-white px-6 transition-opacity"
                            >
                              <CaretDownOutlined
                                className={`text-lg leading-5 transition-transform duration-300 ${
                                  openSubMenus.has(item.id) ? "rotate-180" : ""
                                }`}
                              />
                            </button>
                          )}
                        </div>
                        {item.attribute && item.attribute.length > 0 && (
                          <div
                            className={`overflow-hidden transition-all duration-500 ${
                              openSubMenus.has(item.id) ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
                            }`}
                          >
                            <ul className="bg-[#18af4e]">
                              {item.attribute.map((subItem, subIndex) => (
                                <li key={subIndex}>
                                  <MenuSubItem
                                    subItem={subItem}
                                    locale={locale}
                                    className="block px-[60px] py-3 text-white hover:text-white text-[15px] leading-[24px] font-normal uppercase"
                                    onClick={handleCloseMenu}
                                  />
                                  {item.attribute && subIndex < item.attribute.length - 1 && (
                                    <div className="h-px bg-white bg-opacity-20 mx-8" />
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {index < structuredMenu.length - 1 && <div className="h-px bg-white bg-opacity-20 mx-10" />}
                      </li>
                    )
                  })}
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
      {/* Spacer for mobile header to prevent content overlap */}
      <div className="block lg:hidden h-[110px]" />
    </>
  )
}

export default Header
